import React from 'react';
import HomePage from '../../pages/HomePage';
import GoalsBoard from '../../pages/Goals/GoalsBoard';
import { Goal, SubGoal, Todo } from '../../services/goalService';
import { HashRouteInfo } from '../../routing/parseHashRoute';
import { TabType } from '../../hooks/useNavigation';
import { newGoalHash } from '../../pages/NewGoal/newGoalHash';

const NewGoalPage = React.lazy(() => import('../../pages/NewGoal/NewGoalPage'));
const TimePage = React.lazy(() => import('../../pages/Time/TimePage'));
const ProgressPage = React.lazy(() => import('../../pages/Progress/ProgressPage'));
const SettingsPage = React.lazy(() => import('../../pages/Settings/SettingsPage'));

export interface AppRoutesProps {
  /** The page's route; while a task is open, the route of the page under its drawer. */
  route: HashRouteInfo;
  /** The page's hash (the one under the task drawer while a task is open). */
  pageHash: string;
  activeTab: TabType | null;
  goals: Goal[];
  loading: boolean;
  error: string | null;
  loadGoals: () => Promise<void>;
  updateGoal: (goalId: string, updates: Partial<Goal>) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  toggleFavorite: (goalId: string) => Promise<void>;
  addSubGoal: (goalId: string, title: string, description: string) => Promise<void>;
  updateSubGoal: (goalId: string, subGoalId: string, updates: Partial<SubGoal>) => Promise<void>;
  deleteSubGoal: (goalId: string, subGoalId: string) => Promise<void>;
  updateTodo: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void>;
  addGoal: (goal: Partial<Goal>) => Promise<Goal>;
  goToGoalsTab: () => void;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
  route,
  pageHash,
  activeTab,
  goals,
  loading,
  error,
  loadGoals,
  updateGoal,
  deleteGoal,
  toggleFavorite,
  addSubGoal,
  updateSubGoal,
  deleteSubGoal,
  updateTodo,
  addGoal,
  goToGoalsTab,
}) => {
  // Every way to a new goal leads to the Yeni hedef page (T-183)
  const newGoal = () => { window.location.hash = newGoalHash(); };
  const newAiGoal = () => { window.location.hash = newGoalHash({ ai: true }); };

  if (route.type === 'new-goal') {
    return <NewGoalPage hash={pageHash} addGoal={addGoal} loadGoals={loadGoals} />;
  }

  // Ayarlar (T-184): the profile and the settings are one page
  if (route.type === 'profile' || route.type === 'settings') {
    return <SettingsPage />;
  }

  // Hedefler (T-182): one page for the list, a goal, a stage's link and the drafts
  const board = (
    <GoalsBoard
      goals={goals}
      loading={loading}
      error={error}
      loadGoals={loadGoals}
      updateGoal={updateGoal}
      deleteGoal={deleteGoal}
      toggleFavorite={toggleFavorite}
      addSubGoal={addSubGoal}
      updateSubGoal={updateSubGoal}
      deleteSubGoal={deleteSubGoal}
      updateTodo={updateTodo}
      onNewGoal={newGoal}
      onOpenDraft={(draftId) => { window.location.hash = newGoalHash({ draft: draftId }); }}
      hash={pageHash}
    />
  );

  if (route.type === 'ai-drafts' || route.type === 'goal-detail' || route.type === 'subgoal-todos') {
    return board;
  }

  switch (activeTab) {
    case 'goals':
      return board;
    // Zaman (T-184): Ongoing and Timely in one page; their old paths open it too
    case 'time':
      return <TimePage goals={goals} onChanged={() => { void loadGoals(); }} />;
    case 'progress':
      return <ProgressPage goals={goals} />;
    default:
      return (
        <HomePage
          goals={goals}
          loading={loading}
          onUpdateGoal={updateGoal}
          onToggleFavorite={toggleFavorite}
          onAddSubGoal={addSubGoal}
          onUpdateSubGoal={updateSubGoal}
          onDeleteSubGoal={deleteSubGoal}
          onUpdateTodo={updateTodo}
          onNavigateToTasks={goToGoalsTab}
          onShowAIModal={newAiGoal}
          onShowForm={newGoal}
          onShowCreationModal={newGoal}
          onTimerChanged={() => { void loadGoals(); }}
        />
      );
  }
};

export default AppRoutes;
